import { reaction } from 'mobx';
import { GameStore } from '../powerOfGodsStores/gameStore';
import { GameView } from '../powerOfGodsViewers/gameView';
import {
  AnimationConfigs,
  cashOutPopUpNames,
  gameGlobalStates,
  PowerOfGodsSoundsNames,
  winTypesEnum,
} from '../powerOfGodsCore/constants/globalConstants';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { powerOfGodsGameService } from '../powerOfGodsService/gameService';
import { ReelController } from './reelController';
import { MatrixController } from './matrixController';
import { ringMaxStates, RINGS } from '../powerOfGodsCore/constants/matrixConstants';
import { isStage } from '../../../core/helpers/isMobile';
import { powerOfGodsStore } from '../powerOfGodsStores/powerOfGodsStore';
import { noWinSymbol, reelSymbolIndexesConfig } from '../powerOfGodsCore/constants/reelConstants';
import { userInfoStore } from '../../../store/userInfoStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { betHistoryStore } from '../../../store/betHistoryStore';

export class GameController {
  view = null;

  store = null;

  reel = null;

  matrix = null;

  reactionDisposers = [];

  initGameStoreAndView() {
    this.store = new GameStore();
    this.view = new GameView();
  }

  setupReactions() {
    this.reactionDisposers.push(
      reaction(
        () => this.reel.store.stopComplete,
        (stopComplete) => stopComplete && this.actionsOnReelStopped()
      )
    );
    this.reactionDisposers.push(
      reaction(
        () => [this.store.winEnded, this.store.bonusSceneFinished],
        async ([winEnded, bonusSceneFinished]) => {
          if (winEnded) {
            let winType;
            const { result } = this.store.spinResult;
            if (result) {
              winType = result.winType;
            }
            if (winTypesEnum[4] === winType) {
              if (bonusSceneFinished) {
                betHistoryStore.getLastBet(2);
              }
            } else {
              betHistoryStore.getLastBet();
            }
          }
        }
      )
    );

    this.reactionDisposers.push(
      reaction(
        () => [this.store.isForceStop, this.reel.store.responseReady],
        ([isForceStop, responseReady]) => {
          if (responseReady) {
            this.matrix.store.setIsZeusDrop(
              true /* this.reel.store.responseData === symbolEnum.zeus */
            );
            this.reel.store.setCanStop(responseReady);
            if (isForceStop && !userSettingsStore.isTurboOn) {
              this.reel.store.setIsForceStop(isForceStop);
            }
          }
        }
      )
    );

    this.reactionDisposers.push(
      reaction(
        () => this.store.isInBonusScene,
        (isInBonusScene) => {
          this.setBonusModeActiveState(isInBonusScene);
        }
      )
    );
    this.reactionDisposers.push(
      reaction(
        () => this.store.gameGlobalState,
        (gameGlobalState) => {
          if (gameGlobalState === gameGlobalStates.bonus.bonusEnd) {
            powerOfGodsStore.showWinPopup();
            this.matrix.resetAfterBonus();
            this.store.setIsInBonusScene(false);
            this.store.setGameGlobalState(gameGlobalStates.none);
            this.reel.store.setIsInBonusScene(false);
            // powerOfGodsStore.setIsAllowBet(false);
            soundService.play(PowerOfGodsSoundsNames.ReverseLoadBigRingAfterBonus);
            powerOfGodsGameService.sendBonusFinished();
          }
        }
      )
    );
    this.reactionDisposers.push(
      reaction(
        () => this.store.mustInitBonusSceneOnInitialState,
        (init) => {
          if (!init || this.store.isInBonusScene) return;
          soundService.play(PowerOfGodsSoundsNames.BonusReelLights, true);
          this.store.setGameGlobalState(gameGlobalStates.bonus.bonusInit);
          this.store.setBonusSceneFinished(false);
          this.reel.store.setResponseData(this.store.bonusResulSymbolFromInitialState);
          this.store.setMustInitBonusSceneOnInitialState(false);
        }
      )
    );
    this.reactionDisposers.push(
      reaction(
        () => this.store.bonusSpinResult,
        (bonusSpinResult) => {
          if (bonusSpinResult) {
            this.reel.store.setCanStretch(true);
            this.handleBonusSpinResult(this.store.bonusSpinResult);
            const Result = this.store.bonusSpinResult;
            this.reel.initFromResponse(Result);
          }
        }
      )
    );

    RINGS.forEach((name) => {
      this.reactionDisposers.push(
        reaction(
          () => this.matrix.store.ringLoadingComplete[name],
          (complete) => {
            if (!complete) return;
            const anyCashOut = RINGS.some((key) => this.matrix.store.ringActionTypes[key]?.cashOut);
            if (anyCashOut && !powerOfGodsStore.isWinPopupVisible) {
              this.forceStop();
              powerOfGodsStore.showWinPopup();
              this.matrix.store.resetAfterCashOut();
              userInfoStore.getBalance();
            }
          }
        )
      );
    });
    this.reactionDisposers.push(
      reaction(
        () =>
          (this.matrix.store.ringState.big === ringMaxStates.big &&
            this.matrix.store.ringLoadingComplete.big &&
            this.store.winEnded) ||
          this.store.mustInitBonusSceneOnInitialState,

        (shouldInitBonus) => {
          if (shouldInitBonus && !this.store.isInBonusScene) {
            this.store.setBonusSceneFinished(false);
            this.store.setGameGlobalState(gameGlobalStates.bonus.bonusInit);
            this.store.setMustInitBonusScene(false);
            soundService.play(PowerOfGodsSoundsNames.BonusReelLights, true);
          }
        }
      )
    );
    RINGS.forEach((name) => {
      if (name === 'big') return;
      const disposer = reaction(
        () => this.matrix.store.ringLoadingComplete[name],
        (ready) => {
          if (ready && this.matrix.store.ringActionTypes[name]?.lineCashOut) {
            powerOfGodsStore.setIsAllowBet(false);
            const popupType = cashOutPopUpNames[name];
            this.store.showLineAutoCashOutPopUp(popupType);
            this.matrix.store.setRingActionTypeLineCashOut(name, false);
          }
        }
      );
      this.reactionDisposers.push(disposer);
    });
  }

  async execute(reelContainer, winContainer, ticker, matrixContainer) {
    this.initGameStoreAndView();
    this.initMatrix(matrixContainer, powerOfGodsStore.initialRingStates);
    this.initReel(reelContainer, winContainer, powerOfGodsStore.initialStateSymbol);
    this.view.setContainers(reelContainer, winContainer);
    this.view.initView(ticker);
    this.setupReactions();
    if (powerOfGodsStore.initialBonusInfo) {
      const { bonusSceneFinished, bonusResult } = powerOfGodsStore.initialBonusInfo;
      this.store.setMustInitBonusSceneOnInitialState(
        !bonusSceneFinished,
        bonusResult + reelSymbolIndexesConfig.bonus[0]
      );
    } else {
      this.store.setGameGlobalState(gameGlobalStates.none);
    }
    this.CheckSound();
    powerOfGodsStore.setIsStoreLoaded(true);
  }

  CheckSound() {
    if (userSettingsStore.isSoundOn) {
      if (!this.store.isInBonusScene) {
        soundService.play(PowerOfGodsSoundsNames.Music, true);
      } else {
        soundService.play(PowerOfGodsSoundsNames.BonusMusic, true);
      }
    }
  }

  initReel(reelContainer, winContainer, initialSymbol) {
    this.reel = new ReelController(reelContainer, winContainer, initialSymbol);
    this.reel.view.initContainer();
  }

  initMatrix(matrixContainer, initialStates) {
    this.matrix = new MatrixController(matrixContainer, initialStates);
  }

  checkPopUpAndClose() {
    const { isWinPopupVisible } = powerOfGodsStore;
    if (isWinPopupVisible) {
      powerOfGodsStore.hideWinPopup();
      return true;
    }
    return false;
  }

  spinAction() {
    const { gameGlobalState, isReelRotating, winEnded } = this.store;
    if (this.checkPopUpAndClose() || !powerOfGodsStore.isAllowBet) return;
    switch (gameGlobalState) {
      case gameGlobalStates.show:
        if (!winEnded) {
          this.skipWinAnimations();
        } else {
          powerOfGodsStore.hideWinPopup();
          this.startSpin();
        }
        soundService.play(PowerOfGodsSoundsNames.SpinButton);
        break;
      case gameGlobalStates.waiting:
        if (isReelRotating) {
          this.forceStop();
          soundService.play(PowerOfGodsSoundsNames.SpinButton);
        }
        break;
      case gameGlobalStates.bonus.bonusInitComplete:
      case gameGlobalStates.none:
        this.startSpin();
        soundService.play(PowerOfGodsSoundsNames.SpinButton);
        break;
      default:
        break;
    }
  }

  async startSpin() {
    if (!this.reel.store.canRotate) return;
    try {
      soundService.play(PowerOfGodsSoundsNames.ReelStart);
      this.store.resetForStartSpin();
      this.store.setIsReelRotating(true);
      this.reel.store.setRotating(true);
      if (!this.store.isInBonusScene) {
        await powerOfGodsGameService.doSpin();
      } else {
        await new Promise((resolve) => {
          setTimeout(resolve, AnimationConfigs.bonusLoopDuration * 1000);
        });
        this.reel.initFromResponse(this.reel.store.responseData);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      if (isStage()) console.error('Error in startSpin call:', err);
    }
  }

  actionsOnReelStopped() {
    this.store.setWinEnded(false);
    // powerOfGodsStore.setIsAllowBet(true);
    if (!this.store.isInBonusScene) {
      if (this.store.spinResult !== noWinSymbol) {
        const { small, middle, big, resultSymbol, winInfo, bonusInfo } =
          this.store.getLinesAndSymbolFromSpinResult();
        this.matrix.handleSpinResult({ big, middle, small }, resultSymbol, winInfo, bonusInfo);
        if (bonusInfo) {
          const symbolToShow = bonusInfo.bonusResult + reelSymbolIndexesConfig.bonus[0];
          this.reel.store.setResponseData(symbolToShow);
        }
      }
      if (this.reel.store.responseData === noWinSymbol) {
        this.matrix.doNoWinAnimation();
      }
      this.store.resetAfterReelStopComplete();
    } else {
      this.store.resetAfterBonusReelStopComplete();
    }

    let nextState;
    if (this.store.isInBonusScene) {
      nextState = gameGlobalStates.bonus.bonusShow;
    } else {
      nextState = gameGlobalStates.show;
    }
    this.store.setGameGlobalState(nextState);
    this.reel.onStopComplete().then(() => {
      if (this.store.skipWin) return;
      this.store.setWinEnded(true);
      if (
        this.store.gameGlobalState !== gameGlobalStates.bonus.bonusInit &&
        this.store.gameGlobalState !== gameGlobalStates.bonus.bonusEnd
      ) {
        this.store.setGameGlobalState(gameGlobalStates.none);
      }
      if (this.store.isInBonusScene) {
        this.store.setGameGlobalState(gameGlobalStates.bonus.bonusEnd);
      }
    });
  }

  doCashOut(values) {
    this.matrix.doMatrixCashOut(values);
  }

  setBonusModeActiveState(state) {
    if (state) {
      powerOfGodsStore.setIsAllowBet(!state);
      this.startSpin();
    }
    this.reel.changeBonusModeActiveState(state);
    this.matrix.changeBonusModeActiveState(state);
    // this.matrix.changeBonusModeActiveState(state);
  }

  async skipWinAnimations() {
    Promise.all([this.matrix.skipWinAccelerationAnimations(), this.reel.skipWinAnimation()]).then(
      async () => {
        this.store.setSkipWin(true);
        this.store.setWinEnded(true);
        if (this.store.isInBonusScene) {
          this.store.setGameGlobalState(gameGlobalStates.bonus.bonusEnd);
        }
        if (
          this.store.gameGlobalState !== gameGlobalStates.bonus.bonusInit &&
          this.store.gameGlobalState !== gameGlobalStates.bonus.bonusEnd &&
          !this.store.mustInitBonusScene
        ) {
          this.store.setGameGlobalState(gameGlobalStates.none);
          await new Promise((resolve) => {
            setTimeout(resolve, 150);
          });
          this.startSpin();
        }
      }
    );
  }

  forceStop() {
    if (!this.store.isForceStop && !userSettingsStore.isTurboOn) {
      this.store.setIsForceStop(true);
    }
  }

  disposeReactions() {
    this.reactionDisposers.dispose();
  }

  handleBonusSpinResult() {
    // eslint-disable-next-line no-console
  }
}

export const gameController = new GameController();
